import type { DiskAdminAuditEntry } from '../Contracts/DiskAdmin'

/** Підписи ключів політики в журналі «Змінено політику диска». */
export const POLICY_KEY_LABELS: Record<string, string> = {
    personalQuotaBytes: 'квота «Мого диска»',
    versionsToKeep: 'версій тримати',
    trashDays: 'строк кошика, дн.',
    inactiveDays: 'поріг «неактивний», дн.',
    offboardingDays: 'строк рішення, дн.',
    reminderDays: 'нагадування, день',
    afterDeadline: 'дія після строку',
    sharedActiveDays: 'вікно «спільне використовується», дн.',
    suspendPublicLinks: 'вимикати публічні посилання',
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    value !== null && typeof value === 'object' && !Array.isArray(value)

/**
 * Що саме сталося — одним рядком під подією журналу: «10 ГБ → 20 ГБ»,
 * «строк кошика, дн.: 30 → 14», назва відкритого файлу, текст прохання.
 * Розміри форматує переданий `bytes` (щоб тест не залежав від компонента).
 */
export const auditDetails = (entry: DiskAdminAuditEntry, bytes: (value: number) => string): string => {
    const payload = isRecord(entry.payload) ? entry.payload : {}
    const quota = (value: unknown): string => (typeof value === 'number' ? bytes(value) : 'за замовчуванням')
    const plain = (key: string, value: unknown): string => {
        if (key === 'personalQuotaBytes' && typeof value === 'number') return bytes(value)
        if (typeof value === 'boolean') return value ? 'так' : 'ні'
        if (Array.isArray(value)) return value.join(', ')

        return String(value)
    }

    switch (entry.event) {
        case 'quota_changed': {
            // Чинна квота `null` — квоти немає зовсім (спільний простір без власної);
            // власна `null` — квота за замовчуванням
            const effective = 'effectiveFrom' in payload || 'effectiveTo' in payload
            const side = (raw: unknown, eff: unknown): string =>
                effective ? (typeof eff === 'number' ? bytes(eff) : 'без квоти') : quota(raw)

            return `${side(payload['from'], payload['effectiveFrom'])} → ${side(payload['to'], payload['effectiveTo'])}${
                payload['group'] === true ? ' · групою' : ''
            }`
        }
        case 'policy_changed': {
            const changes = isRecord(payload['changes']) ? payload['changes'] : {}

            return Object.entries(changes)
                .map(([key, change]) =>
                    isRecord(change)
                        ? `${POLICY_KEY_LABELS[key] ?? key}: ${plain(key, change['from'])} → ${plain(key, change['to'])}`
                        : (POLICY_KEY_LABELS[key] ?? key)
                )
                .join('; ')
        }
        case 'file_opened': {
            const node = isRecord(payload['node']) ? payload['node'] : {}

            return typeof node['name'] === 'string' ? node['name'] : ''
        }
        case 'cleanup_requested':
            return typeof payload['message'] === 'string' && payload['message'] !== '' ? `«${payload['message']}»` : 'без повідомлення'
        default:
            return ''
    }
}
